import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import { Link } from 'react-router-dom';
import axios from 'axios';

const Dashboard = () => {
  const { user } = useAuth();
  const { connected, onSyncStatus, onNewSOS } = useSocket();
  const [stats, setStats] = useState({
    troops: 0,
    sos: 0,
    tabs: 0,
    confirmedTabs: 0
  });
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    // Socket-Event-Listener für Echtzeit-Updates
    const unsubscribeSyncStatus = onSyncStatus((data) => {
      if (data && typeof data === 'object') {
        addNotification({
          type: 'sync',
          message: `Sync für Welt ${data.world}: ${data.status}`,
          timestamp: new Date()
        });
      }
    });

    const unsubscribeNewSOS = onNewSOS((data) => {
      if (data && data.targetCoords) {
        addNotification({
          type: 'sos',
          message: `Neue SOS: ${data.targetCoords.x}|${data.targetCoords.y}`,
          timestamp: new Date()
        });
        // Stats aktualisieren
        setStats(prev => ({ ...prev, sos: prev.sos + 1 }));
      }
    });

    return () => {
      if (unsubscribeSyncStatus) unsubscribeSyncStatus();
      if (unsubscribeNewSOS) unsubscribeNewSOS();
    };
  }, [onSyncStatus, onNewSOS]);

  const loadDashboardData = async () => {
    try {
      const response = await axios.get('/api/dashboard/stats');
      if (response.data?.data) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Dashboard-Daten:', error);
    } finally {
      setLoading(false);
    }
  };

  const addNotification = (notification) => {
    setNotifications(prev => [notification, ...prev.slice(0, 4)]); // Nur die letzten 5 behalten
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleTimeString('de-DE');
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'sync': return '🔄';
      case 'sos': return '🚨';
      default: return 'ℹ️';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mb-4"></div>
          <p className="text-gray-600">Lade Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        
        {/* Header */}
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            Willkommen zurück, {user?.username}!
          </h1>
          <p className="text-xl text-gray-600">
            Deine Verteidigungszentrale für Welt {user?.world || 'keine'}
          </p>
          
          {/* Connection Status */}
          <div className="mt-4 flex items-center justify-center">
            <div className={`flex items-center px-4 py-2 rounded-full ${
              connected ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              <div className={`w-3 h-3 rounded-full mr-2 ${
                connected ? 'bg-green-500' : 'bg-red-500'
              }`}></div>
              {connected ? 'Verbunden' : 'Nicht verbunden'}
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="card text-center">
            <div className="text-3xl mb-2">⚔️</div>
            <div className="text-3xl font-bold text-blue-600 mb-1">{stats.troops}</div>
            <div className="text-gray-600">Truppen-Dumps</div>
          </div>
          
          <div className="card text-center">
            <div className="text-3xl mb-2">🚨</div>
            <div className="text-3xl font-bold text-red-600 mb-1">{stats.sos}</div>
            <div className="text-gray-600">SOS-Meldungen</div>
          </div>
          
          <div className="card text-center">
            <div className="text-3xl mb-2">📋</div>
            <div className="text-3xl font-bold text-yellow-600 mb-1">{stats.tabs}</div>
            <div className="text-gray-600">Tabs gesamt</div>
          </div>
          
          <div className="card text-center">
            <div className="text-3xl mb-2">✅</div>
            <div className="text-3xl font-bold text-green-600 mb-1">{stats.confirmedTabs}</div>
            <div className="text-gray-600">Bestätigte Tabs</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* Quick Actions */}
          <div className="card">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Schnellzugriff</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Link to="/sos-input" className="btn-danger text-center">
                🚨 SOS eingeben
              </Link>
              <Link to="/troop-input" className="btn-primary text-center">
                ⚔️ Truppen eingeben
              </Link>
              <Link to="/tab-calculation" className="btn-success text-center">
                📊 Tabs berechnen
              </Link>
              {(user?.role === 'admin' || user?.role === 'superadmin') && (
                <Link to="/admin" className="btn-secondary text-center">
                  👑 Admin-Bereich
                </Link>
              )}
            </div>
          </div>

          {/* Live Notifications */}
          <div className="card">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Live-Benachrichtigungen</h2>
            {notifications.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-4xl mb-4">🔔</div>
                <p className="text-gray-500">Keine neuen Benachrichtigungen</p>
              </div>
            ) : (
              <div className="space-y-3">
                {notifications.map((notification, index) => (
                  <div key={index} className={`p-4 rounded-lg border-l-4 ${
                    notification.type === 'sos' ? 'bg-red-50 border-red-400' :
                    notification.type === 'sync' ? 'bg-blue-50 border-blue-400' :
                    'bg-gray-50 border-gray-400'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <span className="text-xl mr-3">{getNotificationIcon(notification.type)}</span>
                        <span className="font-medium text-gray-800">{notification.message}</span>
                      </div>
                      <span className="text-sm text-gray-500">{formatDate(notification.timestamp)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* User Info */}
        <div className="card">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Spielerinformationen</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h3 className="font-semibold text-gray-700 mb-2">Benutzername</h3>
              <p className="text-gray-600">{user?.username}</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-700 mb-2">Welt</h3>
              <p className="text-gray-600">{user?.world || 'Nicht zugewiesen'}</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-700 mb-2">Spieler-ID</h3>
              <p className="text-gray-600">{user?.dsSpielerId || 'Nicht zugewiesen'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
